import { type ReactElement, useEffect, useState } from 'react';
import { type CourierDispatchJob, type DirectoryRealm, type RideSupportSummary } from '@vello/api-client';
import { isIso4217Currency } from '@vello/core';
import { formatMoney } from '@vello/format';
import { Badge, Button, Text, RecordPanel, RecordRow, RecordRowButton, Ink } from '@vello/web-theme';
import {
  PERSON_COURIER_JOBS_LIMIT,
  PERSON_RIDES_LIMIT,
  courierJobStuck,
  courierJobsOfPerson,
  formatMarketInstant,
  marketDisplayName,
  marketTimeZone,
  personRidesFilterQuery,
  personRidesQuery,
  rideStatusTone,
  shortId,
} from '../../src/index';
import { useAdmin } from '../session';
import { useRouter } from '../router';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { useCourierStep } from '../i18n/courier-step';
import { LoadingBlock } from './loading-block';

/** The ride search matches an account id as rider AND as driver; a record lists only the rides the person took on the side the record is about. */
export function ridesOfPerson(rides: readonly RideSupportSummary[], personId: string, realm: DirectoryRealm): readonly RideSupportSummary[] {
  return rides.filter((ride) => (realm === 'DRIVER' ? ride.driverId === personId : ride.riderId === personId));
}

/** The search asks for the side of the record (`role`); the page is a little wider than the panel lists, and {@link ridesOfPerson} still guards a server that ignores the side. */
const PERSON_RIDES_FETCH = 25;

export function PersonRidesPanel({ personId, realm = 'RIDER' }: { personId: string; realm?: DirectoryRealm }): ReactElement {
  const { runtime, scope, markets } = useAdmin();
  const { navigate } = useRouter();
  const { t, lang } = useT();
  const { fail, v } = useVocab();
  const market = scope.current.city;
  const timeZone = marketTimeZone(market, markets);

  const [rides, setRides] = useState<readonly RideSupportSummary[] | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    const query = personRidesQuery(personId, market);
    if (query === undefined) {
      setRides(undefined);
      setError(undefined);
      return;
    }
    let cancelled = false;
    void (async () => {
      const res = await runtime.rideSupport.search(query.q, { market: query.market, limit: PERSON_RIDES_FETCH, ...(realm === 'DRIVER' || realm === 'RIDER' ? { role: realm } : {}) });
      if (cancelled) return;
      if (res.ok) {
        setRides(ridesOfPerson(res.value.rides, personId, realm).slice(0, PERSON_RIDES_LIMIT));
        setError(undefined);
      } else {
        setRides([]);
        setError(t('prides.refused', { code: fail(res.error) }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [runtime, personId, realm, market, t]);

  const money = (m: { readonly amountMinor: string; readonly currency: string }): string =>
    /^-?\d+$/.test(m.amountMinor) && isIso4217Currency(m.currency)
      ? formatMoney({ amountMinor: BigInt(m.amountMinor), currency: m.currency }, { locale: lang, timeZone: 'UTC', measurement: 'metric' })
      : `${m.amountMinor} ${m.currency}`;

  return (
    <RecordPanel title={t('prides.title')} actions={market !== undefined ? <Text role="caption" tone="secondary">{t('prides.market', { market: marketDisplayName(market, markets) })}</Text> : undefined}>
        {market === undefined ? (
          <Text as="p" role="caption" tone="secondary">{t('prides.needsMarket')}</Text>
        ) : error !== undefined ? (
          <Text as="p" role="caption" tone="secondary">{error}</Text>
        ) : rides === undefined ? (
          <LoadingBlock lines={2} />
        ) : rides.length === 0 ? (
          <Text as="p" role="caption" tone="secondary">{t('prides.empty')}</Text>
        ) : (
          <>
            {rides.map((ride) => (
              <RecordRowButton
                key={ride.rideId}
                aria-label={t('prides.open', { code: ride.code })}
                onClick={() => navigate('ride_detail', { id: ride.rideId })}
              >
                <Ink strong mono>{ride.code}</Ink>
                <Badge tone={rideStatusTone(ride.status)}>{v('ride.status', ride.status)}</Badge>
                <span className="vello-grow" />
                <span className="tnum">{money(ride.fare)}</span>
                <Text role="caption" tone="secondary" mono>{formatMarketInstant(ride.requestedAt, timeZone, lang)}</Text>
              </RecordRowButton>
            ))}
            <Button variant="ghost" onClick={() => navigate('rides_grid', {}, personRidesFilterQuery({ personId, role: realm === 'DRIVER' ? 'DRIVER' : 'RIDER' }))}>{t('prides.all')}</Button>
          </>
        )}
    </RecordPanel>
  );
}

export function PersonCourierPanel({ personId }: { personId: string }): ReactElement {
  const { runtime, scope, markets } = useAdmin();
  const { navigate } = useRouter();
  const { t, lang } = useT();
  const { fail } = useVocab();
  const courierStep = useCourierStep();
  const market = scope.current.city;
  const timeZone = marketTimeZone(market, markets);

  const [jobs, setJobs] = useState<readonly CourierDispatchJob[] | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (market === undefined) {
      setJobs(undefined);
      setError(undefined);
      return;
    }
    let cancelled = false;
    void (async () => {
      const res = await runtime.courierDispatch.jobs(market);
      if (cancelled) return;
      if (res.ok) {
        setJobs(courierJobsOfPerson(res.value.jobs, personId).slice(0, PERSON_COURIER_JOBS_LIMIT));
        setError(undefined);
      } else {
        setJobs([]);
        setError(t('pcour.refused', { code: fail(res.error) }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [runtime, personId, market, t]);

  return (
    <RecordPanel title={t('pcour.title')} actions={market !== undefined ? <Text role="caption" tone="secondary">{t('prides.market', { market: marketDisplayName(market, markets) })}</Text> : undefined}>
        {market === undefined ? (
          <Text as="p" role="caption" tone="secondary">{t('pcour.needsMarket')}</Text>
        ) : error !== undefined ? (
          <Text as="p" role="caption" tone="secondary">{error}</Text>
        ) : jobs === undefined ? (
          <LoadingBlock lines={2} />
        ) : jobs.length === 0 ? (
          <Text as="p" role="caption" tone="secondary">{t('pcour.empty')}</Text>
        ) : (
          <>
            {jobs.map((job) => {
              const stuck = courierJobStuck(job);
              return (
                <RecordRow key={job.jobId} dense>
                  <Ink strong mono>{job.orderCode ?? shortId(job.sourceOrderId)}</Ink>
                  <span>{courierStep(job)}</span>
                  <span className="vello-grow" />
                  {stuck?.kind === 'pin_locked' && <Badge tone="danger">{t('cjobs.stuck.pinLocked')}</Badge>}
                  {stuck?.kind === 'pickup_locked' && <Badge tone="danger">{t('cjobs.stuck.pickupLocked')}</Badge>}
                  {stuck?.kind === 'idle' && <Badge tone="warning"><span>{t('cjobs.stuck.idle', { minutes: stuck.minutes })}</span></Badge>}
                  <Text role="caption" tone="secondary" mono>{formatMarketInstant(job.updatedAt, job.timezone ?? timeZone, lang, 'time')}</Text>
                </RecordRow>
              );
            })}
            <Button variant="ghost" onClick={() => navigate('courier_jobs')}>{t('pcour.all')}</Button>
          </>
        )}
    </RecordPanel>
  );
}
